import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useEffect, useState } from 'react';
import {
  AUTOCOMPLETE_ATTRIBUTE,
  VALIDATION_ERROR_REQUIRED,
} from 'utility/constants';
import { Field } from '../types';

const LOADING_INDICATOR_SIZE = 18;

const useStyles = makeStyles({
  loadingIndicator: {
    position: 'absolute',
    right: 36,
    top: '50%',
    marginTop: -(LOADING_INDICATOR_SIZE / 2),
    marginRight: 0,
    marginLeft: 0,
    pointerEvents: 'none',
  },
});

export interface Props extends Field {
  getOptionDisabled?: (option: any) => boolean;
  getOptionId: (option: any) => string;
  getOptionLabel: (option: any) => string;
  hasError?: boolean;
  loading?: boolean;
  loadingText?: string;
  noOptionsText?: string;
  onChange: (value: any | undefined, name: string) => void;
  options: any[];
  value: string | undefined;
}

const validate = (value: string | undefined, required?: boolean) => {
  if (required && !value) {
    return VALIDATION_ERROR_REQUIRED;
  }
};

const Typeahead: React.FC<Props> = ({
  disabled,
  getOptionDisabled,
  getOptionId = (concept) => concept?.coding?.[0]?.code,
  getOptionLabel = (concept) =>
    concept?.coding?.[0]?.display || concept?.coding?.[0]?.code || '',
  hasError = false,
  label,
  loading = false,
  loadingText = 'Loading…',
  name,
  noOptionsText,
  onChange,
  onValidate,
  options,
  required,
  value,
}) => {
  const classes = useStyles();
  const [showErrors, setShowErrors] = useState(false);

  const handleChange = (event: object, value: any) => {
    onChange(value, name);
  };

  useEffect(() => {
    if (hasError) {
      setShowErrors(true);
    }
  }, [hasError]);

  const error = validate(value, required) || '';
  const isValid = !error;

  useEffect(() => {
    onValidate(isValid);
  }, [isValid, onValidate]);

  const selectedConcept =
    options.find((concept: fhir.CodeableConcept) => {
      return getOptionId(concept) === value;
    }) || null;

  return (
    <FormControl
      fullWidth
      size="small"
      variant="filled"
      required={required}
      error={showErrors && !!error}
    >
      <Autocomplete
        autoComplete={false}
        options={options}
        getOptionLabel={getOptionLabel}
        getOptionDisabled={getOptionDisabled}
        onChange={handleChange}
        value={selectedConcept}
        disabled={disabled}
        loading={loading}
        loadingText={loadingText}
        noOptionsText={noOptionsText}
        renderInput={(params) => {
          if (
            params &&
            params.inputProps &&
            params.inputProps['autoComplete']
          ) {
            params.inputProps['autoComplete'] = AUTOCOMPLETE_ATTRIBUTE;
          }

          return (
            <TextField
              {...params}
              data-field-name={name}
              autoComplete={AUTOCOMPLETE_ATTRIBUTE}
              label={label}
              size="small"
              required={required}
              fullWidth
              variant="filled"
              onFocus={() => setShowErrors(false)}
              onBlur={() => setShowErrors(true)}
              error={showErrors && !!error}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress
                        className={classes.loadingIndicator}
                        color="inherit"
                        size={LOADING_INDICATOR_SIZE}
                        aria-label={loadingText}
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
      />
      {showErrors && !!error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default React.memo(Typeahead);
